/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import { autoUpdate, flip, offset, shift, useClick, useClientPoint, useDismiss, useFloating, useInteractions, useRole, useTransitionStyles, } from '@floating-ui/react';
import { tokens } from '@neo4j-ndl/base';
import { useMemo, useState } from 'react';
export function usePopover({ isInitialOpen = false, placement = 'bottom', isOpen: controlledOpen, onOpenChange: setControlledOpen, offsetOption = 10, anchorElement, anchorPosition, anchorElementAsPortalAnchor, shouldCaptureFocus, initialFocus, role: roleProp, closeOnClickOutside, strategy = 'absolute', } = {}) {
    var _a;
    const [isOpenUncontrolled, setIsOpenUncontrolled] = useState(isInitialOpen);
    const [labelId, setLabelId] = useState();
    const [descriptionId, setDescriptionId] = useState();
    const isOpen = controlledOpen !== null && controlledOpen !== void 0 ? controlledOpen : isOpenUncontrolled;
    const setIsOpen = setControlledOpen !== null && setControlledOpen !== void 0 ? setControlledOpen : setIsOpenUncontrolled;
    const data = useFloating({
        open: isOpen,
        onOpenChange: (open, event) => {
            setIsOpen(open);
            setControlledOpen === null || setControlledOpen === void 0 ? void 0 : setControlledOpen(open, event);
        },
        placement: placement,
        middleware: [
            offset(offsetOption),
            flip({
                crossAxis: placement.includes('-'),
                fallbackAxisSideDirection: 'end',
                padding: 5,
            }),
            shift({ padding: 5 }),
        ],
        elements: {
            reference: anchorElement,
        },
        whileElementsMounted: autoUpdate,
        strategy,
    });
    const context = data.context;
    const click = useClick(context, {
        enabled: controlledOpen === undefined,
    });
    const dismiss = useDismiss(context, {
        outsidePress: closeOnClickOutside,
    });
    const role = useRole(context, {
        role: roleProp,
    });
    const clientPoint = useClientPoint(context, {
        x: anchorPosition === null || anchorPosition === void 0 ? void 0 : anchorPosition.x,
        y: anchorPosition === null || anchorPosition === void 0 ? void 0 : anchorPosition.y,
        enabled: anchorPosition !== undefined,
    });
    const interactions = useInteractions([click, dismiss, role, clientPoint]);
    const { styles: transitionStyles } = useTransitionStyles(context, {
        duration: (_a = Number.parseInt(tokens.transitions.values.duration.quick, 10)) !== null && _a !== void 0 ? _a : 0,
    });
    return useMemo(() => (Object.assign(Object.assign(Object.assign({ isOpen,
        setIsOpen }, interactions), data), { transitionStyles,
        labelId,
        descriptionId,
        setLabelId,
        setDescriptionId,
        anchorElementAsPortalAnchor,
        shouldCaptureFocus,
        initialFocus })), [
        isOpen,
        setIsOpen,
        interactions,
        data,
        transitionStyles,
        labelId,
        descriptionId,
        anchorElementAsPortalAnchor,
        shouldCaptureFocus,
        initialFocus,
    ]);
}
//# sourceMappingURL=use-popover.js.map